import { useCallback, useMemo } from "react";

import { useDestinationVendors } from "@prequel/react";
import {
  ButtonStyle,
  MenuButton,
  MenuItem,
  MenuItemStyle,
  TableCell,
  TableCellType,
  TableRow,
  XIcon,
} from "@prequel/shared-components";

import { SmallCell } from "../../../../shared/components/src/components/Table";
import {
  linkToIntegrityJob,
  linkToTransfer,
  summarizeIntegrityJob,
  summarizeTransfer,
  sumRowsCompared,
} from "./helpers";

import { useDestinationActivities } from "@/api/queries/destination-activities.ts";
import { useAllDestinations } from "@/api/queries/destinations";
import { useAllRecipients } from "@/api/queries/recipients";
import { useCancelTransfer } from "@/api/queries/transfers";
import ResourceTable from "@/components/ResourceTable";
import TransferStatusBadge, { TransferStatus, TransferStatusLabel } from "@/components/TransferStatusBadge";
import { env } from "@/env";
import {
  DataIntegrityJobSummary,
  DestinationActivity,
  getDuration,
  getRelativeSubmittedAt,
  TransferSummary,
} from "@/types";

// Trailing empty column is used for action buttons
const HEADERS_SINGLE_DESTINATION = ["Type", "Status", "Total Rows", "Submitted", "Duration", "Summary", ""];
const HEADERS_MULTI_DESTINATION = ["Type", "Recipient name", "Destination name", "Status", "Total Rows", "Submitted"];

export type ActivitiesTableProps = {
  destinationId?: string;
  emptyMessage?: string;
};

const ActivitiesTable = ({ destinationId, emptyMessage = "No activity found." }: ActivitiesTableProps) => {
  const useQuery = (params: { cursor?: string; pageSize?: number }, filters?: Record<string, string>) => {
    return useDestinationActivities(destinationId, params, filters);
  };

  const cancelTransferMutation = useCancelTransfer();
  const makeTransferActionsMenu = useCallback(
    (cancelButton: MenuItem) => {
      return (
        <div className="m-auto flex text-xs">
          <MenuButton
            type={ButtonStyle.TERTIARY}
            disabled={cancelTransferMutation.isPending}
            spin={true}
            menuItems={[cancelButton]}
            icon={<XIcon className="my-1 h-4 w-3 text-red-600" aria-hidden="true" />}
          />
        </div>
      );
    },
    [cancelTransferMutation.isPending],
  );

  const makeTransferCancelButton = useCallback(
    (transferID: string, status: string) => ({
      label: status === "RUNNING" ? "Cancel running transfer" : "Cancel transfer",
      style: status === "RUNNING" ? MenuItemStyle.DANGER : MenuItemStyle.PRIMARY,
      onClick: () => {
        cancelTransferMutation.mutate({ transferID: transferID });
      },
    }),
    [cancelTransferMutation],
  );

  const mapTransferToRow = useCallback(
    (transfer: TransferSummary, recipientName: string, destinationName: string): TableRow | null => {
      let cells: TableCell[];
      if (destinationId) {
        const cancelButton = makeTransferCancelButton(transfer.id, transfer.status);
        const actionsMenu = makeTransferActionsMenu(cancelButton);
        const showActionsMenu = transfer.status === "PENDING" || transfer.status === "RUNNING";
        cells = [
          { value: "Transfer" },
          { value: <TransferStatusBadge status={transfer.status} /> },
          SmallCell(transfer.rows_transferred.toLocaleString()),
          SmallCell(getRelativeSubmittedAt(transfer.submitted_at)),
          SmallCell(getDuration(transfer)),
          SmallCell(summarizeTransfer(transfer)),
          { type: TableCellType.BUTTON, value: showActionsMenu && actionsMenu },
        ];
      } else {
        cells = [
          { value: "Transfer" },
          { value: recipientName },
          { value: destinationName },
          { value: <TransferStatusBadge status={transfer.status} /> },
          SmallCell(transfer.rows_transferred.toLocaleString()),
          SmallCell(getRelativeSubmittedAt(transfer.submitted_at)),
        ];
      }
      return {
        id: transfer.id,
        type: "transfer_job",
        to: linkToTransfer(transfer),
        cells: cells,
      };
    },
    [destinationId, makeTransferCancelButton, makeTransferActionsMenu],
  );

  const mapIntegrityJobToRow = useCallback(
    (job: DataIntegrityJobSummary, recipientName: string, destinationName: string): TableRow | null => {
      let cells: TableCell[];
      if (destinationId) {
        cells = [
          { value: "Data Integrity Check" },
          { value: <TransferStatusBadge status={job.status} /> },
          SmallCell(sumRowsCompared(job).toLocaleString()),
          SmallCell(getRelativeSubmittedAt(job.submitted_at)),
          SmallCell(getDuration(job)),
          SmallCell(summarizeIntegrityJob(job)),
          { value: null }, // Actions menu should be empty for data integrity jobs
        ];
      } else {
        cells = [
          { value: "Data Integrity Check" },
          { value: recipientName },
          { value: destinationName },
          { value: <TransferStatusBadge status={job.status} /> },
          SmallCell(sumRowsCompared(job).toLocaleString()),
          SmallCell(getRelativeSubmittedAt(job.submitted_at)),
        ];
      }
      return {
        id: job.id,
        to: linkToIntegrityJob(job) ?? "",
        type: "data_integrity_job",
        cells: cells,
      };
    },
    [destinationId],
  );

  const mapActivityToRow = useCallback(
    (activity: DestinationActivity): TableRow | null => {
      const transfer = activity.transfer_job;
      const integrityJob = activity.data_integrity_job;
      const recipientName = activity.recipient_name ?? "";
      const destinationName = activity.destination_name ?? "";

      if (transfer) {
        return mapTransferToRow(transfer, recipientName, destinationName);
      } else if (integrityJob) {
        return mapIntegrityJobToRow(integrityJob, recipientName, destinationName);
      }

      return null;
    },
    [mapTransferToRow, mapIntegrityJobToRow],
  );

  const { data: allRecipients, isLoading: isRecipientsLoading } = useAllRecipients();
  const { data: allDestinations, isLoading: isDestinationsLoading } = useAllDestinations();
  const vendors = useDestinationVendors(env.VITE_APP_API_SERVER);
  const filterFields = useMemo(makeFilterFields, [
    allRecipients,
    isRecipientsLoading,
    allDestinations,
    isDestinationsLoading,
    vendors,
    destinationId,
  ]);

  function makeFilterFields() {
    const typeField = {
      key: "type",
      label: "Type",
      queryParam: "type",
      options: [
        { value: "transfer_job", label: "Transfer" },
        { value: "data_integrity_job", label: "Data Integrity Check" },
      ],
    };
    const statusField = {
      key: "status",
      label: "Status",
      queryParam: "status",
      options: Object.values(TransferStatus).map((s) => ({ value: s, label: TransferStatusLabel[s] })),
    };
    const recipientIdField = {
      key: "recipient_id",
      label: "Recipient ID",
      queryParam: "recipient_id",
      loading: isRecipientsLoading,
      options: (allRecipients ?? [])
        .map((r) => ({ value: r.id, label: r.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    };
    const recipientNameField = {
      key: "recipient_name",
      label: "Recipient Name",
      queryParam: "recipient_name",
      loading: isRecipientsLoading,
      options: (allRecipients ?? [])
        .map((r) => ({ value: r.name, label: r.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    };
    const destinationIdField = {
      key: "destination_id",
      label: "Destination ID",
      queryParam: "destination_id",
      loading: isDestinationsLoading,
      options: (allDestinations ?? [])
        .map((d) => ({ value: d.id, label: d.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    };
    const destinationNameField = {
      key: "destination_name",
      label: "Destination Name",
      queryParam: "destination_name",
      loading: isDestinationsLoading,
      options: (allDestinations ?? [])
        .map((d) => ({ value: d.name, label: d.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    };
    const vendorField = {
      key: "vendor",
      label: "Vendor",
      queryParam: "vendor",
      options: (vendors ?? []).map((v) => ({ value: String(v.key), label: v.display })),
    };

    if (destinationId) {
      return [typeField, statusField];
    } else {
      return [
        typeField,
        statusField,
        recipientIdField,
        recipientNameField,
        destinationIdField,
        destinationNameField,
        vendorField,
      ];
    }
  }

  return (
    <ResourceTable<DestinationActivity>
      columns={destinationId ? HEADERS_SINGLE_DESTINATION : HEADERS_MULTI_DESTINATION}
      useQuery={useQuery}
      rowMapper={mapActivityToRow}
      emptyMessage={emptyMessage}
      filterFields={filterFields}
    />
  );
};

export default ActivitiesTable;
